// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useEffect, useCallback } from "react";
import {
  RefreshCw,
  Download,
  AlertCircle,
  Zap,
  CheckCircle,
  XCircle,
  Activity,
  Mail,
  RotateCcw,
  Settings,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import DataTable from "react-data-table-component";
import apiAnalyticsService from "../../services/llmUsageService";
import useTenants from "../../hooks/useTenants";
import GlobalAdminHeader from "../../components/admin/GlobalAdminHeader";
import PageLoader from "../../components/PageLoader";
import { useRoutes } from "../../hooks/useRoutes";
import { hasSuperAdminAccess } from "../../config/roleConfig";
import {
  STANDARD_DATE_RANGES,
  getDefaultStandardRange,
} from "../../utils/dateRanges";
import { getAdminTenantConfig } from "../../utils/tenantConfig";
import {
  buildDateParams,
  isCustomRangeIncomplete,
  handleRangeChange,
} from "../../utils/dateFilterHelpers";
import { extractPaginatedResponse } from "../../utils/apiHelper";
import { useTheme } from "../../context/ThemeContext";

const APIAnalyticsReports = () => {
  const { userRoles } = useRoutes();
  const { isDarkMode } = useTheme();
  const isSuperAdmin = hasSuperAdminAccess(userRoles || []);

  const {
    tenantOptions,
    selectedTenant,
    setSelectedTenant,
    loading: tenantsLoading,
    isAllTenantsSelected,
  } = useTenants(getAdminTenantConfig(isSuperAdmin));
  const [selectedRange, setSelectedRange] = useState(getDefaultStandardRange());
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [error, setError] = useState(null);
  const [apiPerformanceData, setApiPerformanceData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [workflowExecutionData, setWorkflowExecutionData] = useState([]);
  const [workflowTotalRecords, setWorkflowTotalRecords] = useState(0);
  const [workflowSummary, setWorkflowSummary] = useState(null);
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isWorkflowLoading, setIsWorkflowLoading] = useState(false);
  const [isDashboardLoading, setIsDashboardLoading] = useState(false);

  // API service function
  const fetchApiPerformanceData = useCallback(
    async (page = 1, perPageCount = 20) => {
      setIsLoading(true);
      try {
        const params = {
          limit: perPageCount,
          offset: (page - 1) * perPageCount,
        };

        // Handle custom date range or use days
        const dateParams = buildDateParams(
          selectedRange,
          customStart,
          customEnd,
        );
        // Map dateParams to the format expected by the API
        if (dateParams.startDate && dateParams.endDate) {
          params.startDate = dateParams.startDate;
          params.endDate = dateParams.endDate;
        } else if (dateParams.days) {
          params.days = dateParams.days;
        }

        // Add tenant filtering if not "All Tenants"
        if (!isAllTenantsSelected()) {
          params.tenant_id = selectedTenant;
        }
        const response = await apiAnalyticsService.getApiPerformance(params);
        const { data, total } = extractPaginatedResponse(response);

        setApiPerformanceData(transformApiPerformanceData(data));
        setTotalRecords(total);
        // Clear error on successful load
        if (error && error.includes("API performance")) {
          setError(null);
        }
      } catch (err) {
        console.error("Error fetching API performance data:", err);
        setApiPerformanceData([]);
        setTotalRecords(0);
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          "Failed to load API performance data. Please try again.";
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [
      selectedRange,
      selectedTenant,
      isAllTenantsSelected,
      customStart,
      customEnd,
    ],
  );

  // API service function for workflow execution metrics
  const fetchWorkflowExecutionData = useCallback(
    async (page = 1, perPageCount = 20) => {
      setIsWorkflowLoading(true);
      try {
        const params = {
          limit: perPageCount,
          offset: (page - 1) * perPageCount,
        };

        // Handle custom date range or use days
        const dateParams = buildDateParams(
          selectedRange,
          customStart,
          customEnd,
        );
        Object.assign(params, dateParams);

        // Add tenant filtering if not "All Tenants"
        if (!isAllTenantsSelected()) {
          params.tenant_id = selectedTenant;
        }

        const response =
          await apiAnalyticsService.getWorkflowExecutionMetrics(params);
        const { data, total } = extractPaginatedResponse(response);
        const summary = Array.isArray(response)
          ? null
          : (response?.summary ?? null);

        setWorkflowExecutionData(transformWorkflowExecutionData(data));
        setWorkflowTotalRecords(total);
        setWorkflowSummary(summary);
        // Clear error on successful load
        if (error && error.includes("workflow execution")) {
          setError(null);
        }
      } catch (err) {
        console.error("Error fetching workflow execution data:", err);
        setWorkflowExecutionData([]);
        setWorkflowTotalRecords(0);
        setWorkflowSummary(null);
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          "Failed to load workflow execution data. Please try again.";
        setError(errorMessage);
      } finally {
        setIsWorkflowLoading(false);
      }
    },
    [
      selectedRange,
      selectedTenant,
      isAllTenantsSelected,
      customStart,
      customEnd,
    ],
  );

  // API service function for dashboard summary
  const mapDaysToDateFilter = (days) => {
    if (days === null || days === undefined) return null;
    if (days === 1 || days === 0) return "today";
    if (days === 7) return "last_7_days";
    if (days === 30) return "last_30_days";
    return `last_${days}_days`;
  };

  const fetchDashboardSummary = useCallback(async () => {
    setIsDashboardLoading(true);
    try {
      if (isCustomRangeIncomplete(selectedRange, customStart, customEnd)) {
        // Require both custom dates before calling the API
        setIsDashboardLoading(false);
        return;
      }

      const tenantId = isAllTenantsSelected() ? null : selectedTenant;
      const params = { tenantId: tenantId || undefined };

      const dateParams = buildDateParams(selectedRange, customStart, customEnd);
      if (dateParams.startDate && dateParams.endDate) {
        params.startDate = dateParams.startDate;
        params.endDate = dateParams.endDate;
      } else if (dateParams.days) {
        params.dateFilter = mapDaysToDateFilter(dateParams.days);
      }

      // Default to last 7 days if no filter provided
      if (!params.dateFilter && !params.startDate) {
        params.dateFilter = "last_7_days";
      }

      const response = await apiAnalyticsService.getDashboardSummary(params);

      if (response && typeof response === "object") {
        setDashboardSummary(response);
        // Clear error on successful load
        if (error && error.includes("dashboard summary")) {
          setError(null);
        }
      } else {
        console.warn("Dashboard summary response is not an object:", response);
        setDashboardSummary(null);
      }
    } catch (err) {
      console.error("Error fetching dashboard summary:", err);
      setDashboardSummary(null);
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to load dashboard summary. Please try again.";
      setError(errorMessage);
    } finally {
      setIsDashboardLoading(false);
    }
  }, [
    selectedTenant,
    isAllTenantsSelected,
    selectedRange,
    customStart,
    customEnd,
  ]);

  // Transform API data for DataTable
  const transformApiPerformanceData = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) return [];

    return apiData.map((item, index) => {
      const formatDuration = (ms) => {
        const n = Number(ms);
        if (!Number.isFinite(n)) return "N/A";
        const val = Math.abs(n);
        if (val < 1000) return `${Math.round(val)}ms`;
        if (val < 60000) return `${(val / 1000).toFixed(1)}s`;
        return `${(val / 60000).toFixed(1)}m`;
      };

      // Create unique ID
      const uniqueId = `${item.endpoint}-${item.method}-${index}`;

      return {
        id: uniqueId,
        endpoint: item.endpoint,
        method: item.method,
        name: item.name,
        volume: item.volume || 0,
        successRate: item.success_rate || 0,
        avgDuration: formatDuration(item.avg_duration_ms || 0),
        maxDuration: formatDuration(item.max_duration_ms || 0),
        errors: item.errors || 0,
        status: item.status || "Unknown",
        referenceId: item.reference_id,
      };
    });
  };

  // Transform workflow execution data for DataTable
  const transformWorkflowExecutionData = (workflowData) => {
    if (!workflowData || !Array.isArray(workflowData)) return [];

    return workflowData.map((item, index) => {
      const formatDuration = (ms) => {
        const n = Number(ms);
        if (!Number.isFinite(n)) return "N/A";
        const val = Math.abs(n);
        if (val < 1000) return `${Math.round(val)}ms`;
        if (val < 60000) return `${(val / 1000).toFixed(1)}s`;
        return `${(val / 60000).toFixed(1)}m`;
      };

      // Create unique ID
      const uniqueId = `${item.execution_type}-${item.status_code}-${index}`;

      return {
        id: uniqueId,
        executionType: item.execution_type,
        status: item.status,
        statusCode: item.status_code,
        statusName: item.status_name,
        totalExecutions: item.total_executions || 0,
        successfulExecutions: item.successful_executions || 0,
        failedExecutions: item.failed_executions || 0,
        avgDuration: formatDuration(item.avg_duration_ms || 0),
        maxDuration: formatDuration(item.max_duration_ms || 0),
        minDuration: formatDuration(item.min_duration_ms || 0),
        successRate: item.success_rate || 0,
        failureRate: item.failure_rate || 0,
      };
    });
  };

  // Fetch data on component mount and when selections change.
  //
  // Deps list only the primitives the fetches actually react to — NOT the
  // useCallback references. Including the callbacks causes the effect to
  // fire twice per primitive change (once for the primitive, once because
  // the callback's own deps regenerated it). The callbacks capture
  // up-to-date values via their own closures.
  useEffect(() => {
    setError(null);
    fetchApiPerformanceData();
    fetchWorkflowExecutionData();
    fetchDashboardSummary();
  }, [selectedRange, selectedTenant, customStart, customEnd]);

  // DataTable columns
  const columns = [
    {
      name: "Endpoint",
      selector: (row) => row.endpoint,
      cell: (row) => (
        <code className="text-sm font-mono text-slate-800 dark:text-slate-200 bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded">
          {row.endpoint}
        </code>
      ),
      sortable: true,
      width: "240px",
    },
    {
      name: "Method",
      selector: (row) => row.method,
      cell: (row) => (
        <span
          className={`inline-block px-2 py-1 rounded text-xs font-semibold uppercase ${
            row.method === "GET"
              ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
              : row.method === "POST"
                ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                : row.method === "PUT"
                  ? "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300"
                  : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
          }`}
        >
          {row.method}
        </span>
      ),
      sortable: true,
      width: "110px",
    },
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
      width: "240px",
    },
    {
      name: "Volume",
      selector: (row) => row.volume,
      cell: (row) => (
        <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
          {row.volume.toLocaleString()}
        </span>
      ),
      sortable: true,
      width: "100px",
    },
    {
      name: "Success Rate",
      selector: (row) => row.successRate,
      cell: (row) => (
        <div className="flex flex-col gap-1">
          <div className="w-24 h-2 bg-gray-200 dark:bg-slate-600 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${
                row.successRate >= 99
                  ? "bg-gradient-to-r from-green-500 to-green-600"
                  : row.successRate >= 98
                    ? "bg-gradient-to-r from-blue-500 to-blue-600"
                    : row.successRate >= 95
                      ? "bg-gradient-to-r from-orange-500 to-orange-600"
                      : "bg-gradient-to-r from-red-500 to-red-600"
              }`}
              style={{ width: `${Math.min(row.successRate, 100)}%` }}
            />
          </div>
          <span className="text-sm text-slate-700 dark:text-slate-300 font-medium">
            {row.successRate.toFixed(1)}%
          </span>
        </div>
      ),
      sortable: true,
      width: "140px",
    },
    {
      name: "Avg Duration",
      selector: (row) => row.avgDuration,
      sortable: true,
      width: "140px",
    },
    {
      name: "Max Duration",
      selector: (row) => row.maxDuration,
      sortable: true,
      width: "140px",
    },
    {
      name: "Errors",
      selector: (row) => row.errors,
      cell: (row) => (
        <span
          className={`text-sm font-medium ${row.errors > 0 ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400"}`}
        >
          {row.errors}
        </span>
      ),
      sortable: true,
      width: "100px",
    },
    {
      name: "Status",
      selector: (row) => row.status,
      cell: (row) =>
        row.status === "Healthy" ? (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 rounded-full text-xs font-medium">
            ✓ Healthy
          </span>
        ) : row.status === "Degraded" ? (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 rounded-full text-xs font-medium">
            ⚠ Degraded
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded-full text-xs font-medium">
            ❌ Failed
          </span>
        ),
      sortable: true,
      width: "140px",
    },
  ];

  // Workflow Execution DataTable columns
  const workflowColumns = [
    {
      name: "Execution Type",
      selector: (row) => row.executionType,
      cell: (row) => (
        <div className="flex items-center gap-3">
          <div
            className={`w-8 h-8 rounded-lg ${
              row.executionType === "API Call"
                ? "bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300"
                : row.executionType === "Email Notification"
                  ? "bg-pink-100 text-pink-600 dark:bg-pink-900/40 dark:text-pink-300"
                  : row.executionType === "Status Transition"
                    ? "bg-green-100 text-green-600 dark:bg-green-900/40 dark:text-green-300"
                    : "bg-orange-100 text-orange-600 dark:bg-orange-900/40 dark:text-orange-300"
            } flex items-center justify-center text-sm`}
          >
            {row.executionType === "API Call" ? (
              <Activity className="w-4 h-4" />
            ) : row.executionType === "Email Notification" ? (
              <Mail className="w-4 h-4" />
            ) : row.executionType === "Status Transition" ? (
              <RotateCcw className="w-4 h-4" />
            ) : (
              <Settings className="w-4 h-4" />
            )}
          </div>
          <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
            {row.executionType}
          </span>
        </div>
      ),
      sortable: true,
      width: "200px",
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      width: "240px",
    },
    {
      name: "Total",
      selector: (row) => row.totalExecutions,
      cell: (row) => (
        <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
          {row.totalExecutions.toLocaleString()}
        </span>
      ),
      sortable: true,
      width: "100px",
    },
    {
      name: "Success",
      selector: (row) => row.successfulExecutions,
      cell: (row) => (
        <span className="text-sm font-medium text-green-600 dark:text-green-400">
          {row.successfulExecutions.toLocaleString()}
        </span>
      ),
      sortable: true,
      width: "130px",
    },
    {
      name: "Failed",
      selector: (row) => row.failedExecutions,
      cell: (row) => (
        <span className="text-sm font-medium text-red-600 dark:text-red-400">
          {row.failedExecutions.toLocaleString()}
        </span>
      ),
      sortable: true,
      width: "100px",
    },
    {
      name: "Success Rate",
      selector: (row) => row.successRate,
      cell: (row) => (
        <div className="flex flex-col gap-1">
          <div className="w-24 h-2 bg-gray-200 dark:bg-slate-600 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${
                row.successRate >= 99
                  ? "bg-gradient-to-r from-green-500 to-green-600"
                  : row.successRate >= 95
                    ? "bg-gradient-to-r from-blue-500 to-blue-600"
                    : row.successRate >= 80
                      ? "bg-gradient-to-r from-orange-500 to-orange-600"
                      : "bg-gradient-to-r from-red-500 to-red-600"
              }`}
              style={{ width: `${Math.min(row.successRate, 100)}%` }}
            />
          </div>
          <span className="text-sm text-slate-700 dark:text-slate-300 font-medium">
            {row.successRate.toFixed(1)}%
          </span>
        </div>
      ),
      sortable: true,
      width: "140px",
    },
    {
      name: "Avg Duration",
      selector: (row) => row.avgDuration,
      sortable: true,
      width: "140px",
    },
    {
      name: "Max Duration",
      selector: (row) => row.maxDuration,
      sortable: true,
      width: "140px",
    },
  ];

  // Custom styles for DataTable (theme-aware)
  const customStyles = {
    table: {
      style: {
        backgroundColor: isDarkMode ? "#1e293b" : "white",
        width: "100%",
        tableLayout: "fixed",
      },
    },
    headRow: {
      style: {
        backgroundColor: isDarkMode ? "#334155" : "#f9fafb",
        borderBottom: isDarkMode ? "2px solid #475569" : "2px solid #e5e7eb",
        minHeight: "48px",
      },
    },
    headCells: {
      style: {
        fontSize: "12px",
        fontWeight: "600",
        color: isDarkMode ? "#94a3b8" : "#64748b",
        textTransform: "uppercase",
        paddingLeft: "16px",
        paddingRight: "16px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
    },
    rows: {
      style: {
        minHeight: "64px",
        borderBottom: isDarkMode ? "1px solid #334155" : "1px solid #f3f4f6",
        backgroundColor: isDarkMode ? "#1e293b" : "transparent",
        "&:hover": {
          backgroundColor: isDarkMode ? "#334155" : "#f9fafb",
        },
      },
    },
    cells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        fontSize: "14px",
        color: isDarkMode ? "#e2e8f0" : "#334155",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
    },
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    fetchApiPerformanceData(page, 20);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    fetchApiPerformanceData(page, newPerPage);
  };

  // Workflow pagination handlers
  const handleWorkflowPageChange = (page) => {
    fetchWorkflowExecutionData(page, 20);
  };

  const handleWorkflowPerRowsChange = (newPerPage, page) => {
    fetchWorkflowExecutionData(page, newPerPage);
  };
  // Transform dashboard summary data to apiMetrics format
  const getApiMetrics = () => {
    if (!dashboardSummary?.metrics) {
      return [];
    }

    const { metrics } = dashboardSummary;

    return [
      {
        label: "API Calls Today",
        icon: Activity,
        value: metrics.api_calls_today?.value || "0",
        change: metrics.api_calls_today?.comparison || "",
        trend: metrics.api_calls_today?.trend || "up",
        color: "cyan",
      },
      {
        label: "Success Rate",
        icon: CheckCircle,
        value: metrics.success_rate?.value || "0%",
        change: metrics.success_rate?.comparison || "",
        trend: metrics.success_rate?.trend || "up",
        color: "green",
      },
      {
        label: "Avg Response Time",
        icon: Zap,
        value: metrics.avg_response_time?.value || "N/A",
        change: metrics.avg_response_time?.comparison || "",
        trend: metrics.avg_response_time?.trend || "down",
        color: "orange",
      },
      {
        label: "Failed Requests",
        icon: XCircle,
        value: metrics.failed_requests?.value || "0",
        change: metrics.failed_requests?.comparison || "",
        trend: "down",
        color: "red",
      },
    ];
  };

  const apiMetrics = getApiMetrics();

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <GlobalAdminHeader
        title="API Analytics Reports"
        subtitle={`Real-time monitoring • Auto-refresh: 30s • Last updated: ${new Date().toLocaleTimeString("de-DE")}`}
        status="All Systems Operational"
        statusColor="green"
        tenantOptions={isSuperAdmin ? tenantOptions : []}
        selectedTenant={isSuperAdmin ? selectedTenant : null}
        onTenantChange={isSuperAdmin ? setSelectedTenant : undefined}
        tenantsLoading={isSuperAdmin ? tenantsLoading : false}
        ranges={STANDARD_DATE_RANGES.map((r) => r.label)}
        selectedRange={selectedRange.label}
        onRangeChange={(value) => {
          const range = STANDARD_DATE_RANGES.find((r) => r.label === value);
          setSelectedRange(range);
          handleRangeChange(range, setCustomStart, setCustomEnd);
        }}
        buttons={[
          {
            text: "Export Live Report",
            icon: <Download className="h-4 w-4" />,
            // TODO: wire up export — no backend endpoint yet
            onClick: () => {},
            className:
              "bg-primary-color text-white px-4 py-2 rounded-md hover:bg-primary-color-dark flex items-center gap-2",
          },
        ]}
      />

      {/* Custom Date Range Inputs */}
      {selectedRange?.label === "Custom Range" && (
        <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 lg:px-6 py-3">
          <div className="flex items-center justify-end space-x-2">
            <span className="text-sm text-gray-600 dark:text-slate-400">
              Custom Date Range:
            </span>
            <input
              type="date"
              className="border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
            />
            <span className="text-gray-500 dark:text-slate-500">to</span>
            <input
              type="date"
              className="border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
            />
          </div>
        </div>
      )}

      {isLoading && isWorkflowLoading && isDashboardLoading && (
        <PageLoader message="Lade API-Metriken..." />
      )}

      {/* Main Content - Show if not all are loading */}
      {(!isLoading || !isWorkflowLoading || !isDashboardLoading) && (
        <div className="mx-auto px-4 lg:px-4 py-4">
          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
                <span className="text-red-800 dark:text-red-200">{error}</span>
              </div>
            </div>
          )}

          {/* API Metrics Grid */}
          {apiMetrics.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
              {apiMetrics.map((metric, idx) => (
                <div
                  key={idx}
                  className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-gray-200 dark:border-slate-700 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="text-sm font-medium text-slate-600 dark:text-slate-400">
                      {metric.label}
                    </div>
                    <div
                      className={`w-10 h-10 rounded-lg bg-gradient-to-br bg-primary-color dark:bg-primary-color/40 flex items-center justify-center text-xl`}
                    >
                      <metric.icon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-slate-800 dark:text-slate-100 mb-2">
                    {metric.value}
                  </div>
                  <div className="flex items-center gap-1 text-sm font-medium text-emerald-600 dark:text-emerald-400">
                    <span>
                      {metric.trend === "up" ? (
                        <TrendingUp className="w-4 h-4" />
                      ) : (
                        <TrendingDown className="w-4 h-4" />
                      )}
                    </span>
                    <span>
                      {metric.change} {metric.detail}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white dark:bg-slate-800 rounded-xl p-8 border border-gray-200 dark:border-slate-700 shadow-sm mb-8 text-center">
              <div className="text-gray-500 dark:text-slate-400 text-lg">
                Loading API metrics...
              </div>
            </div>
          )}

          {/* API Endpoint Performance */}
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-gray-200 dark:border-slate-700 shadow-sm mb-8">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                API Endpoint Performance
              </h2>
            </div>
            <div className="w-full overflow-x-auto">
              <DataTable
                columns={columns}
                data={apiPerformanceData}
                keyField="id"
                customStyles={customStyles}
                selectableRows={false}
                pagination
                paginationServer
                paginationTotalRows={totalRecords}
                paginationPerPage={20}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
                paginationRowsPerPageOptions={[20, 30, 50, 75, 100]}
                paginationComponentOptions={{
                  rowsPerPageText: "Rows per page:",
                  rangeSeparatorText: "of",
                }}
                noDataComponent={
                  <div className="py-8 text-center text-gray-500 dark:text-slate-400">
                    No API performance data found
                  </div>
                }
                progressPending={isLoading}
                progressComponent={
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 text-blue-600 dark:text-blue-400 animate-spin" />
                  </div>
                }
                responsive
                highlightOnHover
                pointerOnHover
                fixedHeader
              />
            </div>
          </div>

          {/* Workflow Execution Metrics */}
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-gray-200 dark:border-slate-700 shadow-sm">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                Workflow Execution Metrics by Type
              </h2>
              {/* Summary Stats */}
              {workflowSummary && (
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3 dark:border dark:border-slate-600">
                    <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
                      Total Executions
                    </div>
                    <div className="text-lg font-bold text-slate-800 dark:text-slate-100">
                      {workflowSummary.total_executions?.toLocaleString() || 0}
                    </div>
                  </div>
                  <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3 dark:border dark:border-slate-600">
                    <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
                      Successful
                    </div>
                    <div className="text-lg font-bold text-green-600 dark:text-green-400">
                      {workflowSummary.total_successful?.toLocaleString() || 0}
                    </div>
                  </div>
                  <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3 dark:border dark:border-slate-600">
                    <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
                      Failed
                    </div>
                    <div className="text-lg font-bold text-red-600 dark:text-red-400">
                      {workflowSummary.total_failed?.toLocaleString() || 0}
                    </div>
                  </div>
                  <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3 dark:border dark:border-slate-600">
                    <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
                      Success Rate
                    </div>
                    <div className="text-lg font-bold text-slate-800 dark:text-slate-100">
                      {workflowSummary.overall_success_rate?.toFixed(1) || 0}%
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="w-full overflow-x-auto">
              <DataTable
                columns={workflowColumns}
                data={workflowExecutionData}
                keyField="id"
                customStyles={customStyles}
                selectableRows={false}
                pagination
                paginationServer
                paginationTotalRows={workflowTotalRecords}
                paginationPerPage={20}
                onChangePage={handleWorkflowPageChange}
                onChangeRowsPerPage={handleWorkflowPerRowsChange}
                paginationRowsPerPageOptions={[20, 30, 50, 75, 100]}
                paginationComponentOptions={{
                  rowsPerPageText: "Rows per page:",
                  rangeSeparatorText: "of",
                }}
                noDataComponent={
                  <div className="py-8 text-center text-gray-500 dark:text-slate-400">
                    No workflow execution data found
                  </div>
                }
                progressPending={isWorkflowLoading}
                progressComponent={
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 text-blue-600 dark:text-blue-400 animate-spin" />
                  </div>
                }
                responsive
                highlightOnHover
                pointerOnHover
                fixedHeader
              />
            </div>
          </div>

          {/* Footer Status Bar */}
          <div className="mt-8 bg-gray-100 dark:bg-slate-800 rounded-lg dark:border dark:border-slate-700 p-4 flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-green-500 dark:bg-green-400 rounded-full animate-pulse mr-2"></div>
                <span className="text-sm text-gray-600 dark:text-slate-400">
                  Live Data
                </span>
              </div>
              <span className="text-sm text-gray-500 dark:text-slate-500">
                Last updated: 2 seconds ago
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <button className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium">
                Export Report
              </button>
              <button className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium">
                Configure Alerts
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default APIAnalyticsReports;
